import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import ThumbsDownIcon from "@redotech/redo-icons/thumbs-down.svg";
import ThumbsUpIcon from "@redotech/redo-icons/thumbs-up.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import {
  AiMerchantApproval,
  ConversationMessage,
} from "@redotech/redo-model/conversation";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { sinkPromise } from "@redotech/util/promise";
import { memo, useCallback, useEffect, useState } from "react";
import * as aiFeedbackCss from "./ai-feedback.module.css";

interface AIFeedbackProps {
  message: ConversationMessage;
  conversationId: string;
  format: "chat-bubble" | "email";
}

export const AIFeedback = memo(function AIFeedback({
  message,
  conversationId,
  format,
}: AIFeedbackProps) {
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const [approval, setApproval] = useState<AiMerchantApproval | undefined>(
    message.aiMerchantApproval,
  );

  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState<string | undefined>(
    message.aiMerchantApprovalReasoning,
  );

  const saveFeedback = useCallback(async () => {
    setIsSavingFeedback(true);
    try {
      await merchantRpcClient.updateAiFeedbackOnMessage({
        conversationId,
        messageId: message._id,
        approval: approval || null,
        reasoning: feedbackText,
      });
    } catch (error) {
      console.error("Failed to update AI feedback:", error);
    } finally {
      setIsSavingFeedback(false);
    }
  }, [approval, conversationId, feedbackText, message._id, merchantRpcClient]);

  useEffect(() => {
    if (feedbackText !== message.aiMerchantApprovalReasoning) {
      const timer = setTimeout(() => {
        sinkPromise(saveFeedback());
      }, 500);

      return () => clearTimeout(timer);
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackText]);

  useEffect(() => {
    if (approval !== message.aiMerchantApproval) {
      sinkPromise(saveFeedback());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approval]);

  const textInputElement = (
    <Flex dir="column" gap="xs" ml="md" mt="lg">
      <div className={aiFeedbackCss.textInput}>
        <RedoTextInput
          autoSave={{ isSaving: isSavingFeedback }}
          fullWidth
          placeholder={
            approval === AiMerchantApproval.APPROVED
              ? "What did you like about this response?"
              : "What did you not like about this response?"
          }
          setValue={setFeedbackText}
          value={feedbackText || ""}
        />
      </div>
      <Text fontSize="xxs" textColor="tertiary">
        {isSavingFeedback ? "Saving..." : "Feedback saved automatically"}
      </Text>
    </Flex>
  );

  return (
    <Flex dir="column" gap="md">
      <Flex align="center" gap="xs">
        {approval !== undefined && format === "chat-bubble" && (
          <>{textInputElement}</>
        )}
        <RedoButton
          hierarchy="tertiary"
          IconLeading={ThumbsUpIcon}
          onClick={() => {
            setApproval(AiMerchantApproval.APPROVED);
            amplitude.logEvent("ai-conversation-feedback", {
              conversationId,
              messageId: message._id,
              feedback: "thumbs_up",
              format,
            });
          }}
          pressed={approval === AiMerchantApproval.APPROVED}
          size="sm"
        />
        <RedoButton
          hierarchy="tertiary"
          IconLeading={ThumbsDownIcon}
          onClick={() => {
            setApproval(AiMerchantApproval.DISAPPROVED);
            amplitude.logEvent("ai-conversation-feedback", {
              conversationId,
              messageId: message._id,
              feedback: "thumbs_down",
              format,
            });
          }}
          pressed={approval === AiMerchantApproval.DISAPPROVED}
          size="sm"
        />
        {approval !== undefined && format === "email" && (
          <>{textInputElement}</>
        )}
      </Flex>
    </Flex>
  );
});
